import { readonly, shallowRef } from 'vue';
import { useRouter, type Router } from 'vue-router';

import type { AuthContextFailure } from '@/entities/auth';

import { fetchAuthContext, useAuthStore } from '@/entities/auth';
import {
  navigateToApplicationUrl,
  resolvePlatformHomeUrl,
  useLocationStorage,
  useOrganizationStorage,
} from '@/shared/config';
import { useToast } from '@/shared/lib/toast';

import type { SessionError, SessionStatus } from './types';

import { logout as logoutApi } from '../api/session-api';
import { runAfterLogoutRequest, runBeforeLogoutRequest } from './logout-lifecycle';

const status = shallowRef<SessionStatus>('initializing');
const error = shallowRef<SessionError | null>(null);
const isLoading = shallowRef(false);
let initializationPromise: Promise<void> | null = null;
let hasHandledExpiredSession = false;

const FALLBACK_SESSION_ERROR: AuthContextFailure = {
  kind: 'invalid-response',
  message: 'Не удалось загрузить данные сессии. Повторите попытку.',
};
const IMPERSONATION_STOP_ERROR = 'Не удалось завершить вход от имени пользователя. Повторите попытку.';
const APPLICATION_CONFIGURATION_ERROR =
  'Не удалось определить адрес приложения. Обновите страницу или обратитесь к администратору.';

const setInitializationFailure = (failure: AuthContextFailure): void => {
  error.value = failure;
  status.value = 'error';
  console.warn('[session.initialize] Session bootstrap failed', {
    kind: failure.kind,
    status: failure.status ?? null,
  });
};

export const transitionToExpiredSession = (): void => {
  const authStore = useAuthStore();
  authStore.clearContext();
  error.value = null;
  status.value = 'ready';
};

export const resetSessionExpiredHandling = (): void => {
  hasHandledExpiredSession = false;
};

/**
 * Единая реакция транспортных interceptors (Apollo/axios) на истёкшую сессию:
 * сброс состояния, переход на login и уведомление пользователя.
 */
export const handleSessionExpired = (router: Router): void => {
  if (hasHandledExpiredSession) {
    return;
  }

  hasHandledExpiredSession = true;
  transitionToExpiredSession();
  void router.push({ name: 'auth.login' });
  useToast().error('Сессия истекла. Пожалуйста, войдите снова');
};

/**
 * Composable для управления сессией пользователя
 * Отвечает за инициализацию, обновление и завершение сессии
 */
export function useSession() {
  const authStore = useAuthStore();
  const router = useRouter();

  /**
   * Инициализирует сессию - загружает данные пользователя, организации и сотрудника
   */
  const initializeSession = (): Promise<void> => {
    if (initializationPromise) {
      return initializationPromise;
    }

    status.value = 'initializing';
    error.value = null;

    const promise = authStore
      .loadContext()
      .then((result) => {
        if (result.status === 'failure') {
          setInitializationFailure(result.failure);
          return;
        }

        status.value = 'ready';
        resetSessionExpiredHandling();
      })
      .catch(() => {
        setInitializationFailure(FALLBACK_SESSION_ERROR);
      })
      .finally(() => {
        if (initializationPromise === promise) {
          initializationPromise = null;
        }
      });

    initializationPromise = promise;

    return promise;
  };

  /**
   * Повторная загрузка данных сессии (например, после обновления профиля)
   */
  const refetchSession = async (): Promise<void> => {
    await initializeSession();
  };

  /**
   * Сброс сессии и редирект на страницу входа
   */
  const resetSession = async (): Promise<void> => {
    transitionToExpiredSession();
    await router.push({ name: 'auth.login' });
  };

  /**
   * Выход из системы
   * Вызывает API logout, сбрасывает состояние и редиректит на login
   */
  const logout = async (): Promise<void> => {
    isLoading.value = true;

    try {
      if (authStore.isImpersonating) {
        let destination: URL;

        try {
          destination = resolvePlatformHomeUrl();
        } catch {
          useToast().error(APPLICATION_CONFIGURATION_ERROR);
          return;
        }

        let stopConfirmed = false;

        try {
          const logoutResult = await logoutApi();
          stopConfirmed = logoutResult.impersonationStopped;
        } catch {
          stopConfirmed = false;
        }

        if (!stopConfirmed) {
          const context = await fetchAuthContext();
          stopConfirmed =
            context.status !== 'failure' &&
            context.status !== 'unauthenticated' &&
            Boolean(context.context.user?.is_super_user) &&
            !context.context.impersonation.active;
        }

        if (!stopConfirmed) {
          useToast().error(IMPERSONATION_STOP_ERROR);
          return;
        }

        useOrganizationStorage().resetOrganizationId();
        useLocationStorage().clearLocationId();

        try {
          navigateToApplicationUrl(destination);
        } catch {
          useToast().error(IMPERSONATION_STOP_ERROR);
        }

        return;
      }

      try {
        await runBeforeLogoutRequest();
        await logoutApi();
      } catch {
        // Local cleanup and redirect remain mandatory if REST logout fails.
      }

      transitionToExpiredSession();
      await runAfterLogoutRequest();
      await router.push({ name: 'auth.login' });
    } finally {
      isLoading.value = false;
    }
  };

  return {
    status: readonly(status),
    isLoading: readonly(isLoading),
    error: readonly(error),
    initializeSession,
    refetchSession,
    resetSession,
    logout,
  };
}
